import type { ContentOrderElement, ContentOrderWithGames } from "@/types/content-order";
import type { GameWithRelations } from "@/types/game";

export interface ContentOrderGameRow {
  order: {
    id: string;
    name: string;
    slug?: string;
    description: string | null;
    display_order: number;
    layout?: "row" | "grid" | "curved_row";
    show_header?: boolean;
    center_when_sparse?: boolean;
    side_padding?: number;
    curve_direction?: "up" | "down";
    enlarge_middle?: boolean;
    grid_slots?: Array<{ cols: 1 | 2 | 3; rows: 1 | 2 | 3 }>;
    elements?: ContentOrderElement[];
  };
  games: GameWithRelations[];
}

function publishedSectionElements(order: ContentOrderWithGames): ContentOrderElement[] {
  return (order.elements ?? []).filter((element) => {
    if (element.content_type !== "game") return Boolean(element.asset_url);
    return Boolean(element.game?.published);
  });
}

/**
 * Transform content orders with games into GameRow format for frontend display.
 * Keeps a section that has published games or image/Rive tiles.
 */
export function organizeContentOrders(
  contentOrders: ContentOrderWithGames[]
): ContentOrderGameRow[] {
  return contentOrders
    .filter((order) => order.active)
    .map((order) => {
      const elements = publishedSectionElements(order);
      return {
        order: {
          id: order.id,
          name: order.name,
          slug: order.slug,
          description: order.description,
          display_order: order.display_order,
          layout: order.layout,
          show_header: order.show_header,
          center_when_sparse: order.center_when_sparse,
          side_padding: order.side_padding,
          curve_direction: order.curve_direction,
          enlarge_middle: order.enlarge_middle,
          grid_slots: order.grid_slots,
          elements,
        },
        games: order.games
          .filter((og) => og.game?.published)
          .sort((a, b) => a.display_order - b.display_order)
          .map((og) => og.game!),
      };
    })
    .filter((order) => order.games.length > 0 || (order.order.elements?.length ?? 0) > 0)
    .sort((a, b) => a.order.display_order - b.order.display_order);
}
